import type { Any } from '@api/orvalFetcher'
import { socketLog, type FullSocketConfig } from '@features/websocket/socketService'
import { store } from '@store/store'

const WS_URL = process.env.NEXT_PUBLIC_WS_URL ?? '/'
export const NOTIFICATIONS_SOCKET_NAME = 'notifications'

const perform = (subscription: Any, action: string, ...params: [{ table: string }?]) => {
  try {
    subscription.perform?.(action, ...params)
  } catch (err) {
    console.error(`subscription perform(${action}) failed`, err)
  }
}

const joinTable =
  (table: string) =>
  (subscription: Any): (() => void) => {
    perform(subscription, 'join_table', { table })

    return () => perform(subscription, 'leave_table', { table })
  }

const idFromPath = (path: string) => path.match(/\/(\d+)$/)?.[1] ?? 'unknown'

const configs: FullSocketConfig[] = [
  {
    name: NOTIFICATIONS_SOCKET_NAME,
    match: [/.*/],
    subscription: () => ({
      channel: 'Admin::NotificationsChannel',
      user_id: store.getState().session.user?.id,
    }),
  },

  {
    name: 'tasks',
    match: ['/tasks'],
    subscription: { channel: 'Admin::Tasks::IndexChannel' },
    onConnect: joinTable('tasks'),
  },

  {
    name: 'tasks/:id',
    match: [/^\/tasks\/\d+$/],
    subscription: (path: string) => {
      const match = path.match(/^\/tasks\/(\d+)/)
      if (!match) {
        console.warn(`No match found for tasks/:id path: ${path}`)

        return { channel: 'Admin::Tasks::ShowChannel' }
      }

      return { channel: 'Admin::Tasks::ShowChannel', id: Number(match[1]) }
    },
    onConnect: (subscription, path) => {
      socketLog(`tasks/:id socket connected ${path} ${subscription?.identifier ?? 'no-identifier'}`)

      return joinTable(`tasks/${idFromPath(path)}`)(subscription)
    },
  },

  {
    name: 'failed_rows',
    // Use RegExp to avoid matching e.g. `/tasks/new`
    match: [/^\/tasks\/\d+$/],
    subscription: (path: string) => {
      const match = path.match(/^\/tasks\/(\d+)/)
      if (!match) {
        return { channel: 'Admin::Tasks::FailedRowsChannel' }
      }

      return {
        channel: 'Admin::Tasks::FailedRowsChannel',
        task_id: Number(match[1]),
      }
    },
    onConnect: (subscription, path) => {
      socketLog(`failed_rows socket connected ${path} ${subscription?.identifier ?? 'no-identifier'}`)
      perform(subscription, 'retrieve_resources')

      return () => perform(subscription, 'leave_table', { table: 'failed_rows' })
    },
  },

  {
    name: 'scheduled_actions',
    match: ['/scheduled_actions'],
    subscription: { channel: 'Admin::ScheduledActions::IndexChannel' },
    onConnect: joinTable('scheduled_actions'),
  },

  {
    name: 'users',
    match: ['/users'],
    subscription: { channel: 'Admin::Users::IndexChannel' },
    onConnect: (subscription) => {
      socketLog(`onConnect users ${subscription.identifier}`)

      return joinTable('users')(subscription)
    },
  },

  {
    name: 'users/:id',
    // Use RegExp to avoid matching e.g. `/users/new`
    match: [/^\/users\/\d+$/],
    subscription: (path: string) => {
      const match = path.match(/^\/users\/(\d+)$/)

      if (!match) {
        console.warn(`No match found for users/:id path: ${path}`)

        return { channel: 'Admin::Users::ShowChannel' }
      }

      return { channel: 'Admin::Users::ShowChannel', id: Number(match[1]) }
    },
    onConnect: (subscription, path) => {
      socketLog(`onConnect users/:id ${subscription.identifier}`)

      return joinTable(`users/${idFromPath(path)}`)(subscription)
    },
  },

  {
    name: 'payouts',
    // `/rebates` is the tradin-only alias of the same screen.
    match: ['/payouts', '/rebates'],
    subscription: { channel: 'Admin::Payouts::IndexChannel' },
    onConnect: joinTable('payouts'),
  },

  {
    name: 'coupons',
    match: ['/coupons'],
    subscription: { channel: 'Admin::Coupons::IndexChannel' },
    onConnect: joinTable('coupons'),
  },

  {
    name: 'coupons/:id',
    match: [/^\/coupons\/\d+$/],
    subscription: (path: string) => {
      const match = path.match(/^\/coupons\/(\d+)$/)
      if (!match) {
        console.warn(`No match found for coupons/:id path: ${path}`)

        return { channel: 'Admin::Coupons::ShowChannel' }
      }

      return { channel: 'Admin::Coupons::ShowChannel', id: Number(match[1]) }
    },
  },

  {
    name: 'discount_tiers',
    match: ['/discount_tiers'],
    subscription: { channel: 'Admin::DiscountTiers::IndexChannel' },
    onConnect: joinTable('discount_tiers'),
  },

  {
    name: 'tags',
    match: ['/payouts', '/rebates', '/users', '/accounts'],
    subscription: { channel: 'Admin::Tags::IndexChannel' },
    onConnect: joinTable('tags'),
  },
]

export const socketConfigs = configs.map((cfg) => ({ ...cfg, url: WS_URL }))
